import {useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {omneticApi} from '@dms/api/base';
import {BRANCH_SOURCING_CACHE_TAGS} from '@dms/api/core';
import {useSetUserSettingsItemMutation} from '@dms/api/customer';
import {setActiveBranchId} from '@dms/api/features';
import {handleApiError, useGetCurrentBranch} from '@dms/shared';

import {ApiError, GtmEvent, pushGtmEvent} from 'shared/web';

const SWITCH_ACTIVE_BRANCH_CACHE_KEY = 'switch-active-branch';

/** Branch-scoped `getUserStats` tag — keep aligned with `getSetUserSettingsInvalidationTags`. */
const BRANCH_USER_CACHE_TAGS = ['User'] as const;

type SwitchActiveBranchOptions = {
  onSuccess?: () => void;
};

/**
 * Persists `active_branch` and updates Redux before the mutation so success-path
 * cache invalidation refetches use the new X-Branch header. Rolls back Redux only
 * for the latest in-flight request if the mutation fails, and invalidates branch-scoped
 * sourcing tags so poller writes during the optimistic window are reconciled.
 *
 * `fixedCacheKey` shares in-flight state across BranchSelect and UserAvatar instances.
 * `inFlightRef` blocks same-tick double invokes before RTK `isLoading` flips.
 */
export function useSwitchActiveBranch() {
  const dispatch = useDispatch();
  const {activeBranchId} = useGetCurrentBranch();
  const [updateUserSettings, {isLoading: isMutationLoading}] = useSetUserSettingsItemMutation({
    fixedCacheKey: SWITCH_ACTIVE_BRANCH_CACHE_KEY,
  });
  const [pendingBranchId, setPendingBranchId] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const inFlightRef = useRef(false);

  const isChangingBranch = isMutationLoading || pendingBranchId !== null;

  const switchActiveBranch = (value: string, options?: SwitchActiveBranchOptions) => {
    if (inFlightRef.current || isMutationLoading || value === activeBranchId) {
      return;
    }

    inFlightRef.current = true;
    const requestId = ++requestIdRef.current;
    const previousBranchId = activeBranchId;

    const gtmEvent: GtmEvent = {
      event: 'button_click',
      event_id: 'top_bar_branch_select',
      previous_value: {
        branchId: activeBranchId,
      },
      new_value: {
        branchId: value,
      },
    };

    setPendingBranchId(value);
    // Set Redux branch before the mutation so invalidation refetches use the new X-Branch header.
    dispatch(setActiveBranchId(value));

    updateUserSettings({
      settingKey: 'active_branch',
      userSettingRequestBody: {value},
    })
      .unwrap()
      .then(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        inFlightRef.current = false;
        setPendingBranchId(null);
        pushGtmEvent(gtmEvent);
        options?.onSuccess?.();
      })
      .catch((error: ApiError) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        inFlightRef.current = false;
        setPendingBranchId(null);
        dispatch(setActiveBranchId(previousBranchId));
        // Pollers may have written target-branch data into cache during the optimistic window.
        dispatch(
          omneticApi.util.invalidateTags([...BRANCH_SOURCING_CACHE_TAGS, ...BRANCH_USER_CACHE_TAGS])
        );
        handleApiError(error);
      });
  };

  return {switchActiveBranch, isChangingBranch, activeBranchId, pendingBranchId};
}
